import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { siInstagram, siSnapchat, siTiktok } from 'simple-icons';
import BrandLogo from './BrandLogo';
import { APP_STORE_URL, MAABAR_CR_NUMBER } from '../lib/maabarContact';
import { SHOW_SOCIAL_LINKS } from '../lib/featureFlags';

// Room for the VAT number on the same line, deliberately left as null rather
// than as a TODO: the bottom row below renders its segment ONLY when this is
// set, so filling it in is the whole change -- no layout work, no new string
// plumbing. Each language supplies its own punctuation, exactly as `cr` does.
const VAT_NUMBER = null;

const CONTACT_EMAIL = 'info@maabar.io';

// The registered legal name, verbatim and untranslated in all three languages —
// a legal name is not a label. It links to the React /about page; the static
// English page lives at /company (public/company.html) and gets its own
// «English» link, which must stay a plain anchor: a router <Link> would hit
// the SPA catch-all and never ask the server for the static file.
const LEGAL_NAME = 'Maabar International Business Company';

/* A registration number, isolated from the text around it.

   The digits are ASCII and stay ASCII in all three languages -- nothing here
   localises them. What `dir="ltr"` prevents is the OTHER failure: inside the
   Arabic (RTL) paragraph the number is a left-to-right run, and the bidi
   algorithm resolves the characters touching it -- the colon, the separating
   dot, a future VAT segment -- against whichever direction wins. That is how a
   label ends up with its punctuation on the wrong side, intermittently and
   only in Arabic. Isolating the run makes ar render like en and zh by
   construction rather than by luck. */
const Num = ({ v }) => <span dir="ltr">{v}</span>;

// Official brand marks, not redrawings: simple-icons ships the paths each brand
// publishes on its own resource page (Instagram -> about.meta.com/brand,
// Snapchat -> snapchat.com/brand-guidelines, TikTok -> tiktok.com). All three
// permit a single-colour mark, which is what the footer shows; the brand colour
// appears on hover only, and the path is never stretched or restyled.
//
// Network names stay untranslated in all three languages — a brand name is part
// of the mark. Only the column heading is trilingual.
const SOCIAL = [
  { key: 'instagram', name: 'Instagram', href: 'https://www.instagram.com/maabar.io', icon: siInstagram },
  { key: 'snapchat', name: 'Snapchat', href: 'https://www.snapchat.com/add/maabar.io', icon: siSnapchat },
  { key: 'tiktok', name: 'TikTok', href: 'https://www.tiktok.com/@maabar.io', icon: siTiktok },
];

// Every path here is a real route in App.js. Deliberately absent: "how it works"
// and "claim your page", which have no static route (claiming is /claim/:slug,
// per factory), and "register as supplier", which the header already offers.
const T = {
  ar: {
    tagline: 'استيراد B2B من المصانع الصينية مباشرة.',
    socialHead: 'تابعنا',
    verifyHead: 'التوثيق',
    // No caption in Arabic: the seal itself is Arabic, so it already reads as
    // «متجر موثّق» to this reader. The caption exists only for the readers the
    // seal cannot speak to — see `sealNote` in en/zh and the comment on
    // sealColumn().
    sealNote: null,
    companyHead: 'الشركة',
    company: [
      { label: 'عن معبر', path: '/about' },
      { label: 'الأسئلة الشائعة', path: '/faq' },
      { label: 'الشروط والأحكام', path: '/terms' },
      { label: 'سياسة الخصوصية', path: '/privacy' },
      { label: 'تواصل معنا', path: '/contact' },
    ],
    getApp: 'حمّل التطبيق',
    soon: 'قريباً',
    // A function, not a label + a space, because the three languages punctuate
    // this differently and a shared template cannot get all three right: Arabic
    // and English want a space after the separator, Chinese uses a FULLWIDTH
    // colon (：) which already carries its own spacing and must not be followed
    // by one. The NUMBER is still a single constant -- only the wrapping moves.
    cr: (n) => <>السجل التجاري: <Num v={n} /></>,
    vat: (n) => <>الرقم الضريبي: <Num v={n} /></>,
    copy: 'معبر © 2026 · جميع الحقوق محفوظة',
  },
  en: {
    tagline: 'Saudi B2B sourcing, straight from Chinese factories.',
    socialHead: 'Follow us',
    verifyHead: 'Verified store · Saudi Business Center',
    sealNote: 'Click to view the certificate',
    companyHead: 'Company',
    company: [
      { label: 'About Maabar', path: '/about' },
      { label: 'FAQ', path: '/faq' },
      { label: 'Terms & Conditions', path: '/terms' },
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Contact Us', path: '/contact' },
    ],
    getApp: 'Get the app',
    soon: 'Soon',
    cr: (n) => <>CR No. <Num v={n} /></>,
    vat: (n) => <>VAT No. <Num v={n} /></>,
    copy: 'Maabar © 2026 · All rights reserved',
  },
  zh: {
    tagline: '沙特 B2B 采购，直连中国工厂。',
    socialHead: '关注我们',
    verifyHead: '沙特商业中心 · 认证商店',
    sealNote: '点击查看认证证书',
    companyHead: '公司',
    company: [
      { label: '关于 Maabar', path: '/about' },
      { label: '常见问题', path: '/faq' },
      { label: '条款与条件', path: '/terms' },
      { label: '隐私政策', path: '/privacy' },
      { label: '联系我们', path: '/contact' },
    ],
    getApp: '下载应用',
    soon: '即将推出',
    cr: (n) => <>商业登记号：<Num v={n} /></>,
    vat: (n) => <>增值税号：<Num v={n} /></>,
    copy: 'Maabar © 2026 · 保留所有权利',
  },
};

const SEAL_SRC = 'https://eauthenticate.saudibusiness.gov.sa/EAuthSealApi/seal.js';

export default function Footer({ lang }) {
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';
  const sealRef = useRef(null);

  /* The Saudi Business Center seal, mounted INSIDE the footer rather than
     floating. Both halves of that are decided by reading seal.js, not by
     deploying and looking:

       var floating = pos.indexOf('bottom') >= 0 || pos.indexOf('top') >= 0;
       if (floating) { f.style.position='fixed'; document.body.appendChild(f); }
       else          { c.appendChild(f); }

     So `data-position` is the entire switch. The snippet as supplied carried
     data-position="bottom-left", which would have pinned it to the viewport;
     the attribute is omitted here so the script appends into this container.
     THE TOKEN IS VERBATIM — only the positioning knob changed, and only
     because in-footer was the placement asked for.

     Dropping the float also avoids a collision the fixed version would have
     had: IdeaToProduct's launcher is fixed at bottom-left, and AIHub's uses
     insetInlineEnd, which resolves to bottom-LEFT under RTL. Bottom-left is
     occupied in Arabic twice over.

     ⚠️ WHY THIS EFFECT EXISTS AT ALL. seal.js runs one querySelectorAll at
     DOMContentLoaded and never observes the DOM again. This footer is rendered
     by React, after that fires, so the tag in index.html is a race it usually
     loses — the container does not exist yet when init() looks for it.
     Re-appending the script re-runs its IIFE and re-scans; mount() is
     idempotent (it sets data-sbc-mounted and returns early), so an already
     mounted seal is untouched and no duplicate iframe appears.

     ⚠️ THE SEAL IS ARABIC-ONLY, AND NO CODE HERE CAN CHANGE THAT. Measured
     2026-09-21 by fetching the iframe URL with our real token at lang=ar, en,
     zh and fr: all four return HTTP 200, 8730 bytes, `<html lang="ar"
     dir="rtl">`, byte-identical apart from a reload timer that drifts ~1s
     between requests. No English string appears anywhere in the document, the
     iframe fetches nothing at runtime, and there is no Content-Language or
     Vary: Accept-Language — sending `Accept-Language: en-US` also returns
     Arabic. So `?lang=` is accepted and ignored.
     Two consequences, both deliberate:
       - The en/zh column heading and `sealNote` below carry the meaning the
         seal cannot. That is the actual fix for a non-Arabic reader.
       - There is NO remount on language change. Tearing the iframe down and
         clearing data-sbc-mounted so it rebuilds with a new ?lang= is
         mechanically correct and was written and rejected: today it costs a
         visible flicker on every language switch and returns the same Arabic
         seal. Revisit together with docs/TECH_DEBT.md when SBC ships a second
         language. */
  useEffect(() => {
    const el = sealRef.current;
    if (!el || el.getAttribute('data-sbc-mounted')) return undefined;
    /* <html lang> has to be correct BEFORE seal.js runs, and it is not, for a
       structural reason: React flushes CHILD effects before PARENT ones. This
       footer is a descendant of App, so this effect always runs before
       App.js's `document.documentElement.lang = lang` — which means seal.js
       would read the PREVIOUS language every single time, and on the very
       first paint the `lang="en"` hard-coded in index.html. Setting it here
       makes the one run that actually mounts see the right value, instead of
       depending on an effect-ordering guarantee that runs the wrong way.
       Same assignment as App.js, not a competing source of truth: App.js still
       owns it for every later change, and both write `lang || 'ar'`.
       It has no visible effect today (the seal ignores ?lang= — see above);
       it is here because it is the correct input, and because <html lang> is
       read by screen readers and translation prompts regardless of the seal. */
    document.documentElement.lang = lang || 'ar';
    const s = document.createElement('script');
    s.src = SEAL_SRC;
    s.async = true;
    // Logged, not swallowed: an adblocker or a CSP change is the likely reason
    // an official seal silently disappears, and security rule 4 says a failed
    // external load says so.
    s.onerror = () => console.error('[Footer] Saudi Business Center seal.js failed to load:', SEAL_SRC);
    document.body.appendChild(s);
    return () => { s.remove(); };
  }, [lang]);

  // NOT a <nav>. index.css:243 styles the bare tag as the site header —
  // position: fixed, top: 0, z-index: 1000 — so a <nav> here is torn out of the
  // footer and pinned over the hero. role="navigation" gives the same landmark
  // without the tag, and nothing in the global sheet targets the role.
  //
  // Links are real anchors rather than buttons calling navigate: a footer is
  // where people middle-click and where crawlers read the site's shape.
  const column = (head, links) => (
    <div className="site-footer-col" role="navigation" aria-label={head}>
      <h3 className="site-footer-head" style={{ fontFamily: font }}>{head}</h3>
      <ul className="site-footer-list site-footer-links">
        {links.map((l) => (
          <li key={l.path}>
            <Link className="site-footer-link" to={l.path} style={{ fontFamily: font }}>{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );

  // Separate from column(): these are external anchors, not routes, and each
  // carries a mark. Every icon is rendered at exactly 22px inside a fixed box so
  // the three rows line up and hovering cannot shift the list.
  const socialColumn = () => (
    <div className="site-footer-col" role="navigation" aria-label={t.socialHead}>
      <h3 className="site-footer-head" style={{ fontFamily: font }}>{t.socialHead}</h3>
      <ul className="site-footer-list site-footer-social">
        {SOCIAL.map((s) => (
          <li key={s.key}>
            <a
              className={`site-footer-link site-footer-social-link is-${s.key}`}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.name}
            >
              <span className="site-footer-social-ic" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" role="img">
                  <path d={s.icon.path} />
                </svg>
              </span>
              <span className="site-footer-social-name">{s.name}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  /* The seal as a footer COLUMN, not a centred line above the bottom row.

     MOVED 2026-09-21. Centred at the bottom it had nowhere to grow: the seal
     expands downward when it opens the QR and the certificate details, and the
     only thing under it was the copyright row and the end of the page. As a
     column it expands into the column's own space and simply makes that grid
     row taller, which pushes the bottom row down instead of colliding with it.

     It carries a heading like every other column so it reads as part of the
     layout rather than as something bolted on, and `align-items: start` on the
     grid puts that heading on the same baseline as «الشركة». */
  const sealColumn = () => (
    <div className="site-footer-col site-footer-seal-col">
      <h3 className="site-footer-head" style={{ fontFamily: font }}>{t.verifyHead}</h3>
      {/* No data-position: that attribute is the entire float/inline switch in
          seal.js, and its absence is what keeps the seal inside this container.
          The token is verbatim. */}
      <div ref={sealRef} className="sbc-verify-seal" data-token="ZXlmT25MUDk2bWhvcTNyelpONnNFUT09"></div>
      {/* en/zh only, and `t.sealNote` is null in ar — see the effect above.
          The seal renders in Arabic for every reader, so an English or Chinese
          visitor sees an unexplained Arabic badge; this line tells them what it
          is and that it opens. NOT a button and not a label-for: the clickable
          thing lives inside a cross-origin iframe we cannot reach, so any
          affordance here would be a lie. It is a caption, and it says so by
          being muted, small and inert. */}
      {t.sealNote && (
        <p className="site-footer-seal-note" style={{ fontFamily: font }}>{t.sealNote}</p>
      )}
    </div>
  );

  return (
    <footer className={`site-footer ${isAr ? 'is-ar' : 'is-ltr'}`}>
      <div className="site-footer-shell">
        {/* THE MODIFIER FLIPPED 2026-09-21, and the reason is the seal.
            It used to be `is-no-social`, narrowing a three-track grid to two
            because hiding the marks left an empty track. The seal now occupies
            exactly the slot the marks vacated, so the default is three tracks
            again -- brand, company, seal -- and nothing has to be narrowed.

            With the marks shown (since 2026-10-15) it is four columns, not
            three: `has-social` is present only while SHOW_SOCIAL_LINKS is
            true, so that flag is the whole switch in either direction. */}
        <div className={`site-footer-cols${SHOW_SOCIAL_LINKS ? ' has-social' : ''}`}>
          <div className="site-footer-col site-footer-brand">
            <BrandLogo size="sm" align={isAr ? 'flex-end' : 'flex-start'} />
            <p className="site-footer-tagline" style={{ fontFamily: font }}>{t.tagline}</p>

            <p className="site-footer-head site-footer-apps-head" style={{ fontFamily: font }}>{t.getApp}</p>
            <div className="site-footer-stores">
              <a className="site-footer-store" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.4 12.8c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.2 1.1 0 1.5-.7 2.8-.7s1.6.7 2.8.7c1.1 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.5-.1 0-2.2-.9-2.2-3.3zM14.3 6c.6-.7 1-1.7.9-2.7-.9 0-2 .6-2.6 1.3-.6.6-1.1 1.7-.9 2.6 1 .1 2-.5 2.6-1.2z" /></svg>
                <span>App Store</span>
              </a>
              {/* A span, not a link: there is nothing to open yet, and the chip says so. */}
              <span className="site-footer-store is-soon" aria-disabled="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3.6 2.4c-.3.3-.5.7-.5 1.2v16.8c0 .5.2.9.5 1.2l.1.1 9.4-9.4v-.2L3.7 2.3l-.1.1zm12.8 6.2L5.4 2.3l8.2 8.2 2.8-1.9zM5.4 21.7l11-6.3-2.8-1.9-8.2 8.2zm14-8.9c.6-.4 1-.9 1-1.5s-.4-1.1-1-1.5l-2.3-1.3-3 3 3 3 2.3-1.7z" /></svg>
                <span>Google Play</span>
                <em style={{ fontFamily: font }}>{t.soon}</em>
              </span>
            </div>
          </div>

          {/* Shown since 2026-10-15 (hidden 2026-09-20 to 2026-10-15). One flag,
              SHOW_SOCIAL_LINKS in lib/featureFlags.js; the app mirrors the same
              three links in its src/lib/socialLinks.js. */}
          {SHOW_SOCIAL_LINKS && socialColumn()}
          {column(t.companyHead, t.company)}
          {sealColumn()}
        </div>

        <div className="site-footer-bottom">
          <p style={{ fontFamily: font }}>{t.copy}</p>
          <span className="site-footer-dot" aria-hidden="true">·</span>
          <Link className="site-footer-mail" to="/about" dir="ltr">{LEGAL_NAME}</Link>
          <span className="site-footer-dot" aria-hidden="true">·</span>
          <a className="site-footer-mail" href="/company" lang="en" dir="ltr">English</a>
          <span className="site-footer-dot" aria-hidden="true">·</span>
          {/* dir="ltr" on the number, in every language. The digits are ASCII
              and stay ASCII, but inside an RTL paragraph a bidi run that starts
              with Arabic text and ends in digits can have its trailing
              punctuation reordered. Isolating the number makes the rendering
              identical in ar, en and zh instead of merely usually identical. */}
          <p style={{ fontFamily: font }}>{t.cr(MAABAR_CR_NUMBER)}</p>
          {/* Renders only when VAT_NUMBER is set. No empty segment, no orphan
              separator, nothing to clean up later -- see the constant. */}
          {VAT_NUMBER && (
            <>
              <span className="site-footer-dot" aria-hidden="true">·</span>
              <p style={{ fontFamily: font }}>{t.vat(VAT_NUMBER)}</p>
            </>
          )}
          <span className="site-footer-dot" aria-hidden="true">·</span>
          <a className="site-footer-mail" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
      </div>

      <style>{`
        /* Every selector below is prefixed and descendant-scoped to .site-footer.
           index.css styles bare footer / nav / section / ul tags for the rest of
           the site, and a two-class selector both outranks those and cannot be
           matched by anything outside this component. */

        /* Overrides the global bare footer rule, which is display:flex with
           space-between and its own padding. */
        .site-footer {
          display: block; width: 100%;
          background: var(--bg-overlay);
          border-top: 1px solid var(--border-subtle);
          padding: 32px 20px 14px;
        }
        .site-footer .site-footer-shell {
          max-width: 1180px; width: 100%; margin: 0 auto;
          display: flex; flex-direction: column; gap: 16px;
        }

        /* Brand column is the wide one; the two link columns split the rest. */
        .site-footer .site-footer-cols {
          display: grid;
          grid-template-columns: minmax(220px, 1.5fr) 1fr 1fr;
          gap: 12px 32px;
          align-items: start;
        }
        /* FOUR tracks while the social marks are shown (SHOW_SOCIAL_LINKS).
           The base rule above is the three-track footer without them (brand,
           company, seal). Direction-agnostic:
           the grid flows with the footer's dir, so ar and ltr both order
           correctly without a separate RTL rule. */
        .site-footer .site-footer-cols.has-social {
          grid-template-columns: minmax(200px, 1.4fr) 1fr 1fr 1fr;
        }
        .site-footer .site-footer-col {
          display: flex; flex-direction: column; min-width: 0;
          position: static;   /* belt and braces against the fixed nav rule */
        }
        .site-footer .site-footer-brand { gap: 8px; }

        /* One line. The 30ch cap that was here forced a wrap in all three
           languages and cost the brand column a whole line of height; nowrap
           with an ellipsis makes that deterministic rather than depending on
           how wide the column happens to be. The phone block puts the wrap
           back, where there is no width to spare. */
        .site-footer .site-footer-tagline {
          margin: 0; max-width: none;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          font-size: 13px; line-height: 1.5; color: var(--text-secondary);
        }

        .site-footer .site-footer-head {
          margin: 0 0 8px;
          font-size: 11px; font-weight: 700;
          letter-spacing: 0.09em; text-transform: uppercase;
          color: var(--text-muted);
        }
        .site-footer .site-footer-apps-head { margin: 4px 0 6px; }

        .site-footer .site-footer-list {
          list-style: none; margin: 0; padding: 0;
          display: flex; flex-direction: column; gap: 8px;
        }
        .site-footer .site-footer-list li { list-style: none; }
        .site-footer .site-footer-link {
          font-size: 14px; line-height: 1.4;
          color: var(--text-secondary); text-decoration: none;
          transition: color 0.2s;
        }
        .site-footer .site-footer-link:hover { color: var(--text-primary); }

        /* Social rows: mark + name on one baseline. The list gap is a little
           wider than the text columns' because each row is icon-height, not
           line-height. */
        .site-footer .site-footer-social { gap: 14px; }
        .site-footer .site-footer-social-link {
          display: inline-flex; align-items: center; gap: 10px;
        }
        /* Fixed 28px box holding a 22px mark. The box — not the svg — carries the
           chip background, so Snapchat's hover chip can appear without the mark
           itself changing size or shape. */
        .site-footer .site-footer-social-ic {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; flex: 0 0 28px;
          border-radius: 8px;
          background: transparent;
          transition: color 0.2s, background-color 0.2s;
        }
        .site-footer .site-footer-social-ic svg { display: block; }

        /* Base state is monochrome in the footer's own text colour; brand colour
           is a hover affordance only. Instagram and TikTok tint the mark itself;
           Snapchat's yellow is illegible on this background, so it gets the dark
           chip the brand guidelines call for instead. */
        .site-footer .site-footer-social-link.is-instagram:hover .site-footer-social-ic { color: #E4405F; }
        .site-footer .site-footer-social-link.is-tiktok:hover .site-footer-social-ic { color: #000; }
        .site-footer .site-footer-social-link.is-snapchat:hover .site-footer-social-ic {
          color: #FFFC00; background: #101010;
        }

        .site-footer .site-footer-stores {
          display: flex; align-items: stretch; gap: 8px; flex-wrap: wrap;
        }
        /* Identical padding plus one min-height, so the "soon" chip inside Google
           Play cannot make that button the taller of the two. */
        .site-footer .site-footer-store {
          display: inline-flex; align-items: center; gap: 7px;
          background: var(--text-primary); color: var(--surface);
          padding: 8px 12px; min-height: 36px; box-sizing: border-box;
          border-radius: 9px;
          font-size: 12px; font-weight: 700; text-decoration: none;
          font-family: var(--font-sans);
          transition: opacity 0.2s;
        }
        .site-footer .site-footer-store:hover { opacity: 0.85; }
        .site-footer .site-footer-store.is-soon {
          background: var(--bg-subtle); color: var(--text-disabled);
          border: 1px solid var(--border-subtle); cursor: default;
        }
        .site-footer .site-footer-store.is-soon:hover { opacity: 1; }
        .site-footer .site-footer-store.is-soon em {
          font-style: normal; font-size: 10px; font-weight: 700;
          background: var(--surface); color: var(--text-secondary);
          border-radius: 999px; padding: 1px 7px;
        }

        /* Row 2: one line under a hairline, 12px below the rule. */
        /* The seal column. The iframe seal.js injects starts at 120x44 and
           RESIZES ITSELF by postMessage when it opens the QR and certificate
           details, so nothing here may constrain its height -- no fixed height,
           no overflow, no align-items: center that would make it grow in both
           directions from the middle. It grows downward from the top of the
           column, into the column's own space. */
        .site-footer .site-footer-seal-col .sbc-verify-seal {
          display: flex;
          align-items: flex-start;
          min-width: 0;
        }
        /* max-width only: the script owns width and height, and fighting it
           would clip the expanded panel. This just stops the expanded state
           overflowing the shell on a narrow phone. */
        .site-footer .site-footer-seal-col .sbc-seal-frame { max-width: 100%; }
        /* The caption sits under the seal and must not push against it while
           the seal is expanding: the iframe resizes itself by postMessage, so
           a plain top margin is the only safe spacing -- nothing here reserves
           height or the expanded panel would be shoved off. Muted and 11px, a
           step below the link colour, so it reads as a note on the badge
           rather than as a fourth footer link. */
        .site-footer .site-footer-seal-note {
          margin: 8px 0 0; font-size: 11px; line-height: 1.45;
          color: var(--text-disabled);
        }
        /* Matches the marks column it replaced: left in ltr, right in ar,
           without a direction-specific rule. */
        .site-footer.is-ar .site-footer-seal-col .sbc-verify-seal { justify-content: flex-end; }
        .site-footer.is-ltr .site-footer-seal-col .sbc-verify-seal { justify-content: flex-start; }

        .site-footer .site-footer-bottom {
          border-top: 1px solid var(--border-subtle);
          padding-top: 12px;
          display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
        }
        /* Each item is unbreakable, so when the row does wrap it breaks between
           items and never mid-sentence. */
        .site-footer .site-footer-bottom p {
          margin: 0; font-size: 11.5px; color: var(--text-disabled);
          white-space: nowrap;
        }
        .site-footer .site-footer-dot { font-size: 11.5px; color: var(--text-disabled); }
        .site-footer .site-footer-mail {
          font-size: 11.5px; color: var(--text-secondary); text-decoration: none;
          font-family: var(--font-sans); direction: ltr;
        }
        .site-footer .site-footer-mail:hover { color: var(--text-primary); text-decoration: underline; }

        /* Direction: the shell inherits dir from the app shell, so the grid and
           the flex rows mirror on their own. Only the brand column's own
           alignment has to be stated. */
        .site-footer.is-ar { text-align: right; }
        .site-footer.is-ltr { text-align: left; }
        .site-footer.is-ar .site-footer-brand { align-items: flex-end; text-align: right; }
        .site-footer.is-ltr .site-footer-brand { align-items: flex-start; text-align: left; }

        /* Tablet: brand keeps its own row, the two link columns share the next. */
        @media (max-width: 900px) {
          .site-footer .site-footer-cols { grid-template-columns: 1fr 1fr; gap: 32px; }
          .site-footer .site-footer-brand { grid-column: 1 / -1; }
          /* With the marks hidden, Company is the only thing on the second row
             and half the row would sit empty beside it. One track, so it sits
             under the brand instead of next to a hole. */
          /* Two tracks here regardless. Without the marks that is company +
             seal on one row; with them it is marks + company, then the seal
             wrapping onto a third row. Both fit, and neither leaves a hole. */
        }

        /* Phone: three full-width centred blocks stacked — brand, then the marks,
           then the company links.

           The two link columns used to sit side by side here, each in half the
           screen, because stacking five company links under three Explore links
           ran past the bottom of the screen. Neither half of that reasoning holds
           now: Explore is gone, and both lists have become single wrapping rows
           rather than columns, so stacked they cost two or three lines between
           them. Half a phone width is also too narrow for "Terms & Conditions"
           to sit on one line, which is what the full width buys back. */
        @media (max-width: 560px) {
          /* ⚠️ The bottom padding is not cosmetic. A FIXED float sits in the
             viewport's bottom corner and overlays whatever the footer has
             there when you scroll to the end: AIHub at insetInlineEnd 24.
             (Until 2026-10-11 SupportWhatsapp held the other bottom corner;
             it now sits at the top under the menu button. The padding stays:
             it is the AI hub's clearance too.)

             The seal stacks last on a phone and expands downward, which put it
             straight into that band. The clearance below turns the overlaid
             strip into empty padding instead of content. calc() rather than a
             flat number so a notched phone's safe area adds to it rather than
             eating it. */
          .site-footer { padding: 24px 16px calc(72px + var(--safe-bottom, 0px)); }
          /* The seal centres with the rest of the stacked blocks here. */
          .site-footer .site-footer-seal-col .sbc-verify-seal { justify-content: center; }
          .site-footer .site-footer-shell { gap: 16px; }
          .site-footer .site-footer-cols { gap: 16px 18px; }
          .site-footer.is-ar .site-footer-brand,
          .site-footer.is-ltr .site-footer-brand {
            align-items: center; text-align: center;
          }
          .site-footer .site-footer-head { margin-bottom: 8px; }
          .site-footer .site-footer-stores { justify-content: center; }

          /* The tagline wraps again here: at this width nowrap would ellipsize
             it, and the brand column has the room for two lines. */
          .site-footer .site-footer-tagline {
            white-space: normal; overflow: visible; font-size: 12.5px;
          }

          /* Each block takes the whole width and centres. The two-column split
             this replaces gave each list about 170px, and that — not the layout
             mode — is what broke "Terms & Conditions" and "Privacy Policy"
             across two lines. A label cannot sit on one line in a box narrower
             than the label. */
          .site-footer .site-footer-cols > .site-footer-col {
            grid-column: 1 / -1;
            align-items: center; text-align: center;
          }

          /* Both lists become one centred row that wraps between items. Each
             link holds its natural width and never breaks inside itself, so a
             line ends at a phrase boundary or not at all. */
          .site-footer .site-footer-links,
          .site-footer .site-footer-social {
            flex-direction: row; flex-wrap: wrap;
            align-items: center; justify-content: center;
            gap: 6px 14px;
          }
          .site-footer .site-footer-links .site-footer-link {
            font-size: 13px; white-space: nowrap;
          }

          /* The marks lose their network names for width — dropped from view,
             not from assistive tech: each link's aria-label already carries the
             name, so the accessible name is unchanged. */
          .site-footer .site-footer-social-name { display: none; }
          .site-footer .site-footer-social-ic {
            width: 34px; height: 34px; flex-basis: 34px;
          }
          .site-footer .site-footer-social-ic svg { width: 26px; height: 26px; }

          .site-footer .site-footer-bottom {
            justify-content: center; text-align: center; gap: 6px;
          }
        }
      `}</style>
    </footer>
  );
}
